import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Fragment, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { useOutlook } from '@hooks/query/useOutlook';
import { useMounted } from '@hooks/useMounted';
import { useScrollPosition } from '@hooks/useScrollPosition';
import { captureException } from '@sentry/nextjs';
import { FontSizes, Spacing } from 'chatie-design-system';

import Adfit from '@components/common/Adfit';
import NativeInFeedAd from '@components/common/NativeInFeedAd';
import BottomButton from '@components/common/BottomButton';
import Empty from '@components/common/Empty';
import HorizontalFilter from '@components/common/HorizontalFilter';
import { LinkItem } from '@components/common/ListItem';
import Loading from '@components/common/Loading';
import { PaginationIterator } from '@components/common/Pagination';
import { PushSize } from '@components/layout/Push';
import OutlookComponent from '@components/outlook/OutlookComponent';

import {
  ChatRoomStatus,
  ChatRoomsQueryType,
  fetchChatRoomDetail,
  fetchChatRooms,
  fetchStoryChatRooms,
  updateChatRoomsState,
} from '@api/chatbot';
import { Platform, QUERY_KEYS } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { media, sticky } from '@styles/css';
import { useAnalytics } from '@utils/analytics';
import {
  getChatRoomLastMessageContent,
  getStoryChatRoomLastMessageContent,
} from '@utils/chat';
import { useMe } from '@utils/hook';
import { ParsedStorage } from '@utils/localStorage';
import { formatComma } from '@utils/misc';
import { useChatPaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

import ChatRoom from './ChatRoom';
import StoryChatRoomCard from './StoryChatRoomCard';

const readStoryFilterCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: row;
  text-align: left;
  padding: ${Spacing.small}px ${Spacing.medium}px;
  border-bottom: 1px solid ${theme.border.light};
  ${sticky(PushSize.tab)};
  z-index: 3;
  background-color: ${theme.var.white};
  height: 43px;

  &::after {
    content: '';
    display: table;
    clear: both;
  }
  .sort-container {
    margin-left: ${Spacing.xsmall}px;
  }

  ${media.tablet} {
    padding: ${Spacing.small}px ${Spacing.xlarge}px;
  }

  ${media.desktop} {
    padding: ${Spacing.small}px ${Spacing.xxlarge}px;
  }
`;

const editButtonGroupCSS = (theme: Theme) => css`
  margin-left: auto;

  span {
    display: inline-block;
    height: 12px;
    border-left: 1px solid ${theme.border.light};
    margin: 0 ${Spacing.xsmall}px;
    vertical-align: middle;
  }
`;

const editButtonCSS = (theme: Theme) => css`
  font-size: ${FontSizes.xsmall}px;
  color: ${theme.var.black};
`;

const chatRoomListContainerCSS = css`
  margin-top: ${Spacing.xhsmall}px;

  li {
    border-bottom: none;
  }

  ${media.tablet} {
    padding: 0 ${Spacing.xlarge}px;
  }

  ${media.desktop} {
    padding: 0 ${Spacing.xxlarge}px;
  }
`;

// const bannerCSS = (theme: Theme) => css`
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
//   padding: 12px 14px;
//   color: ${theme.var.white};

//   background: ${SUBSCRIPTION_GRADIENT_COLOR};

//   p {
//     ${Typography.h6}

//     display: flex;
//     justify-content: center;
//     flex: 1;
//     font-size: 12px;
//     text-align: center;
//   }
// `;

const chatRoomListCss = css`
  margin-top: ${Spacing.small}px;
`;

const emptyCSS = (sort: 'character' | 'story') => css`
  .empty {
    ${sort === 'character' ? `padding: 0` : ''};
  }

  .outlook-container {
    display: flex;
    flex-direction: column;
    gap: ${Spacing.small}px;
    padding: 0 0 0 ${Spacing.xxsmall}px;
    margin-top: 10px;
  }
`;

const bottomBtnCSS = (isCheming?: boolean) => css`
  .bottomButton {
    ${isCheming ? 'margin-bottom: 55px;' : ''}
  }
`;

const ChatRoomList = ({
  isAd = true,
  hasEditBtn = true,
  characterId,
  onOccureClickChatRoom,
  isCheming = false,
}: {
  isAd?: boolean;
  hasEditBtn?: boolean;
  characterId?: number;
  onOccureClickChatRoom?: (chatRoomId: number) => void;
  isCheming?: boolean;
}) => {
  const { t } = useTranslation();
  const { firebase } = useAnalytics();
  const { mutateAsync: updateChatRooms } = useMutation(updateChatRoomsState);
  const { platform } = useGlobalContext();
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState(false);
  const [ids, setIds] = useState<number[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sort, setSort] = useState<'character' | 'story'>('character');
  const [storyIds, setStoryIds] = useState<number[]>([]);

  // const isOverVersion =
  //   platform !== Platform.ios &&
  //   checkRequiredAppVersion(platform, version, SUBSCIRBE_VERSION);

  const chatRoomsQueries: ChatRoomsQueryType = {
    ordering: 'last_chat_newest',
    status: 'AT' as ChatRoomStatus,
    character_id: characterId,
  };

  if (characterId) {
    chatRoomsQueries.character_id = characterId;
  }

  const queryKey = characterId
    ? QUERY_KEYS.CHAT_ROOM.ACTIVE_CHARACTER_PAGINATION(characterId)
    : QUERY_KEYS.CHAT_ROOM.LIST('Library');
  const storyQueryKey = QUERY_KEYS.CHAT_ROOM.STORY_LIST;
  const isMounted = useMounted();
  const query = useChatPaginationQuery(
    queryKey,
    ({ pageParam }) => fetchChatRooms(chatRoomsQueries, pageParam),
    {
      refetchOnWindowFocus: 'always',
      enabled: !!isMounted && sort === 'character',
      onSuccess(data) {
        const pages = data.pages;
        if (pages && isMounted) {
          const fetchedIds = pages?.map((page) =>
            page.items?.map((item) => item.id)
          );
          setIds(fetchedIds?.flat());
          setIsSelectAll(false);
        }
      },
    }
  );
  const storyQuery = useChatPaginationQuery(
    storyQueryKey,
    ({ pageParam }) => fetchStoryChatRooms(pageParam),
    {
      enabled: !!isMounted && sort === 'story' && !isCheming,
      refetchOnWindowFocus: 'always',

      onSuccess(data) {
        const pages = data.pages;
        if (pages && isMounted) {
          const fetchedIds = pages?.map((page) =>
            page.items?.map((item) => item.id)
          );
          setStoryIds(fetchedIds?.flat());
          setIsSelectAll(false);
        }
      },
    }
  );

  useScrollPosition('chatList');

  const isQueryDataEmpty = query ? query.isFetched && query.count === 0 : false;
  const { usePageOutlook } = useOutlook();
  const outlook = usePageOutlook('library-play', {
    enabled: isQueryDataEmpty,
  });

  const queryClient = useQueryClient();
  const { web } = useWebView();
  const router = useRouter();
  const { data: me } = useMe();

  const handleShouldRefreshChange = (event: StorageEvent) => {
    if (event.key === 'shouldRefresh') {
      if (event.newValue === 'true') {
        ParsedStorage.setItem('shouldRefresh', 'false');
        queryClient.invalidateQueries(QUERY_KEYS.USER.SOCIAL_PROVIDER);
      }
    }
  };

  const handleClickContinuteButton = async (
    chatRoomId: number,
    storyId?: number,
    episodeId?: number,
    title?: string
  ) => {
    if (sort === 'story') {
      if (!storyId || !episodeId) {
        return alert('접근할 수 없는 채팅방입니다');
      }
      if (platform === Platform.web) {
        router.push(
          `/stories/${storyId}/episodes/${episodeId}/chat-rooms/${chatRoomId}?mode=continue${
            title ? `&title=${title}` : ''
          }`
        );
      } else {
        web.openPage(
          `/stories/${storyId}/episodes/${episodeId}/chat-rooms/${chatRoomId}?mode=continue${
            title ? `&title=${title}` : ''
          }`
        );
      }
      return;
    }
    setIsLoading(true);

    const res = await queryClient.fetchQuery(
      QUERY_KEYS.CHAT_ROOM.DETAIL(chatRoomId),
      async () => await fetchChatRoomDetail(chatRoomId)
    );

    if (res && me) {
      setIsLoading(false);

      if (onOccureClickChatRoom) {
        onOccureClickChatRoom(chatRoomId);
      }

      setTimeout(() => {
        if (platform === Platform.web) {
          router.push(`/play/chat-rooms/${res.id}?mode=continue`);
        } else {
          web.openPage(`/play/chat-rooms/${res.id}?mode=continue`);
        }
      }, 300);
    }
    setIsLoading(false);
  };

  const handleSelect = (id: number) => {
    const isCheck = selectedIds.includes(id);
    if (!isCheck) {
      setSelectedIds((prev) => [...prev, id]);
      if (selectedIds.length + 1 === ids.length) {
        setIsSelectAll(true);
      }
    } else {
      const updatedSelectedIds = selectedIds.filter((itemId) => itemId !== id);
      setSelectedIds(updatedSelectedIds);
      setIsSelectAll(false);
    }
  };

  const handleToggleSelectAllButton = () => {
    if (isSelectAll) {
      setSelectedIds([]);
      setIsSelectAll(false);
    } else {
      const allIds = sort === 'character' ? [...ids] : [...storyIds];
      setSelectedIds([...allIds]);
      setIsSelectAll(true);
    }
  };

  const handleToggleEditButton = () => {
    if (sort === 'character' ? !ids.length : !storyIds?.length) {
      return;
    }

    if (isEdit) {
      setSelectedIds([]);
      setIsSelectAll(false);
    }

    setIsEdit(!isEdit);
  };

  const handleClickMoveToIntro = (id: number) => {
    setTimeout(() => {
      if (platform === Platform.web) {
        router.push(`/play/characters/${id}`);
      } else {
        web.openPage(`/play/characters/${id}`);
      }
    }, 300);
  };

  const handleClickDeleteButton = async () => {
    if (confirm('해당 채팅을 삭제하시겠습니까?')) {
      const chunkArray = (array: number[], chunkSize: number): number[][] => {
        const chunks: number[][] = [];
        for (let i = 0; i < array.length; i += chunkSize) {
          chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
      };

      const chunkedSelectedIds = chunkArray(selectedIds, 50);
      const updatedIds = [] as number[];

      try {
        for (const chunk of chunkedSelectedIds) {
          const payload = {
            chat_room_ids: chunk,
            status: 'IAT' as ChatRoomStatus,
          };

          await updateChatRooms(payload, {
            onSuccess() {
              if (sort === 'character') {
                queryClient.removeQueries(queryKey);
              } else {
                queryClient.removeQueries(storyQueryKey);
              }
              updatedIds.push(...chunk);

              setIsSelectAll(false);
              setSelectedIds([]);
            },
          });
        }

        firebase.logEvent('chat_room_inactive', {
          user_id: me?.id as number,
          inactivation_count: selectedIds.length,
          total_count: ids.length,
        });
      } catch (err) {
        captureException(err);

        firebase.logEvent('chat_room_inactive', {
          user_id: me?.id as number,
          inactivation_count: selectedIds.length,
          total_count: updatedIds.length,
        });
      }
    }
  };

  // const handleClickPromotion = () => {
  //   if (!isOverVersion) {
  //     const text =
  //       platform === Platform.ios
  //         ? 'IOS는 아직 준비중이에요'
  //         : '앱을 최신버전으로 업데이트 해주세요!';
  //     return alert(text);
  //   }
  //   if (platform === Platform.web) {
  //     router.push('/payment?tab=subscription');
  //   }
  //   web.openPage('/payment?tab=subscription');
  // };

  useEffect(() => {
    if (sort === 'character') {
      const pages = query.data?.pages;
      if (pages) {
        const fetchedIds = pages?.map((page) =>
          page.items?.map((item) => item.id)
        );
        setIds(fetchedIds?.flat());
        setIsSelectAll(false);
      }
    } else {
      const pages = storyQuery.data?.pages;
      if (pages) {
        const fetchedIds = pages?.map((page) =>
          page.items?.map((item) => item.id)
        );
        setStoryIds(fetchedIds?.flat());
      }
    }
  }, [query.data, storyQuery.data, sort]);

  useEffect(() => {
    const currentIds = sort === 'character' ? ids : storyIds;
    if (!currentIds.length) {
      setIsEdit(false);
    }

    if (selectedIds.length === currentIds.length) {
      setIsSelectAll(true);
    } else {
      setIsSelectAll(false);
    }
  }, [selectedIds, ids, storyIds]);

  useEffect(() => {
    window.addEventListener('storage', handleShouldRefreshChange);

    return () => {
      window.removeEventListener('storage', handleShouldRefreshChange);
    };
  }, []);

  return (
    <>
      <div css={readStoryFilterCSS}>
        {isLoading && <Loading center />}
        {!isEdit ? (
          <>
            <HorizontalFilter
              options={[
                {
                  label: `전체(${formatComma(
                    sort === 'character'
                      ? query?.data?.pages[0].total_count ?? 0
                      : storyQuery?.data?.pages[0]?.total_count ?? 0
                  )})`,
                  value: 'latest',
                },
              ]}
            />
            {!isCheming && (
              <div className="sort-container">
                <HorizontalFilter
                  options={[
                    { label: t('캐릭터'), value: 'character' },
                    { label: t('작품'), value: 'story' },
                  ]}
                  selected={sort}
                  onChange={setSort}
                />
              </div>
            )}
          </>
        ) : (
          <HorizontalFilter
            options={[
              {
                label: `선택됨(${formatComma(selectedIds?.length)})`,
                value: 'latest',
              },
            ]}
          />
        )}
        <div css={editButtonGroupCSS}>
          {isEdit && hasEditBtn && (
            <>
              <Fragment>
                <button
                  css={editButtonCSS}
                  onClick={handleToggleSelectAllButton}
                >
                  {t(isSelectAll ? '전체선택 해제' : '전체선택')}
                </button>
              </Fragment>
              <span />
            </>
          )}
          {hasEditBtn &&
            (sort === 'character' ? query.isFetched : storyQuery?.isFetched) &&
            (sort === 'character'
              ? query.count > 0
              : storyQuery?.count > 0) && (
              <Fragment>
                <button css={editButtonCSS} onClick={handleToggleEditButton}>
                  {t(isEdit ? '취소' : '편집')}
                </button>
              </Fragment>
            )}
        </div>
      </div>
      <div css={chatRoomListContainerCSS}>
        {sort === 'character' && (
          <PaginationIterator
            name="chat-list"
            query={query}
            ad={isAd && <Adfit type="library" />}
            inFeedAd={isAd && <NativeInFeedAd unitKey="library_mobile" />}
          >
            {(item) => {
              if (!item?.last_chat_message) {
                return;
              }

              const regex =
                /\{\{TextButton\s+link='([^']*)'\s+text='([^']*)'\}\}/;
              const match = item?.last_chat_message?.content?.match(regex);

              let lastMessage;

              if (match) {
                const [, , text] = match;
                lastMessage = text;
              } else {
                lastMessage = getChatRoomLastMessageContent(
                  item?.last_chat_message?.content
                );
              }

              const isAdultChatRoom = !!item?.is_sexual;

              const chatRoominfo = {
                characterName: item?.character?.name,
                lastMessage: lastMessage,
                lastMessageDate: item?.last_chat_message?.created_at,
                id: item?.id,
                image: item?.character?.profile_image,
                peek: item?.peek,
              };

              return (
                <>
                  {/* {index === 0 && isOverVersion && !isCheming && (
                    <div css={bannerCSS} onClick={handleClickPromotion}>
                      <p>
                        {t(
                          '월 3,900원에 매일 5개씩 총 150개의 대화권을 준다고? 👀'
                        )}
                      </p>
                      <Icon
                        icon={ArrowRight}
                        color={theme.var.white}
                        size={Sizes.medium}
                      />
                    </div>
                  )} */}
                  <div css={chatRoomListCss} key={item.id}>
                    <LinkItem
                      hideArrow
                      newTab
                      left={
                        <ChatRoom
                          chatRoomInfo={chatRoominfo}
                          isEdit={isEdit}
                          isCheck={selectedIds.includes(chatRoominfo.id)}
                          key={chatRoominfo.id}
                          isAdultChatRoom={isAdultChatRoom}
                          isDeletedCharacter={!item?.character}
                          isMyCharacter={
                            me?.id === item?.character?.player?.user_id
                          }
                        />
                      }
                      onClick={() =>
                        isEdit
                          ? handleSelect(chatRoominfo.id)
                          : handleClickContinuteButton(chatRoominfo.id)
                      }
                    />
                  </div>
                </>
              );
            }}
          </PaginationIterator>
        )}
        {sort === 'story' && (
          <PaginationIterator
            name="story-chat-list"
            query={storyQuery}
            ad={isAd && <Adfit type="library" />}
            inFeedAd={isAd && <NativeInFeedAd unitKey="library_mobile" />}
          >
            {(item) => {
              if (!item?.last_chat_message) {
                return;
              }
              const lastMessage = getStoryChatRoomLastMessageContent(
                item?.last_chat_message?.content
              );

              const chatRoominfo = {
                characterName: item?.episode?.profile?.name,
                lastMessage: lastMessage,
                lastMessageDate: item?.last_chat_message?.created_at,
                id: item?.id,
                image: item?.episode?.profile?.profile_image,
              };

              return (
                <>
                  {/* {index === 0 && isOverVersion && !isCheming && (
                    <div css={bannerCSS} onClick={handleClickPromotion}>
                      <p>
                        {t(
                          '월 3,900원에 매일 5개씩 총 150개의 대화권을 준다고? 👀'
                        )}
                      </p>
                      <Icon
                        icon={ArrowRight}
                        color={theme.var.white}
                        size={Sizes.medium}
                      />
                    </div>
                  )} */}
                  <div css={chatRoomListCss} key={item.id}>
                    <LinkItem
                      hideArrow
                      newTab
                      left={
                        <StoryChatRoomCard
                          chatRoomInfo={chatRoominfo}
                          isEdit={isEdit}
                          isCheck={selectedIds.includes(chatRoominfo.id)}
                          key={chatRoominfo.id}
                          story={item?.story}
                        />
                      }
                      onClick={() =>
                        isEdit
                          ? handleSelect(chatRoominfo.id)
                          : handleClickContinuteButton(
                              item?.id,
                              item?.episode?.story_id,
                              item?.episode?.episode_id,
                              item?.story?.title
                            )
                      }
                    />
                  </div>
                </>
              );
            }}
          </PaginationIterator>
        )}
        {isEdit && (
          <div css={bottomBtnCSS(isCheming)}>
            <BottomButton
              className={'bottomButton'}
              text={t('선택 항목 삭제')}
              onClick={handleClickDeleteButton}
              disabled={!selectedIds?.length}
            />
          </div>
        )}
        <div css={emptyCSS(sort)}>
          {/* {!isCheming &&
            ((sort === 'story' && storyQuery.count === 0) ||
              (sort === 'character' && query.count === 0)) &&
            isOverVersion && (
              <div css={bannerCSS} onClick={handleClickPromotion}>
                <p>
                  {t('월 3,900원에 매일 5개씩 총 150개의 대화권을 준다고? 👀')}
                </p>
                <Icon
                  icon={ArrowRight}
                  color={theme.var.white}
                  size={Sizes.medium}
                />
              </div>
            )} */}
          <Empty
            query={sort === 'character' ? query : storyQuery}
            hideIcon={sort === 'character' ? true : false}
            className="empty"
          >
            {sort === 'character' ? (
              <div className="outlook-container">
                <PaginationIterator
                  name="play-outlook-list"
                  query={outlook}
                  identifier={'key'}
                >
                  {(item, index) => {
                    return (
                      <>
                        <OutlookComponent
                          component={item}
                          key={index}
                          order={index}
                          onClickProfile={handleClickMoveToIntro}
                        />
                      </>
                    );
                  }}
                </PaginationIterator>
              </div>
            ) : (
              <p className="multiline">
                {t(`작품에서도 플레이 가능한 거, 알고 계셨나요?
              회차의 마지막에서 플레이할 수 있어요!`)}
              </p>
            )}
          </Empty>
        </div>
      </div>
    </>
  );
};

export default ChatRoomList;
